import { useEffect, useState } from 'react';
import Link from '@/components/compat/Link';
import { ArrowRight, BookOpen, Eye, Star } from 'lucide-react';
import {
  getRelatedGuides,
  type RelatedGuideCard,
} from '@/services/guide-views.service';
import { getGuideHref } from '@/lib/guide-routes';
import { categoryColors } from '@/lib/articles';
import { formatReadingTimeLabel } from '@/utils/readingTime';
import { formatViewCount } from '@/utils/formatViews';
import { cn } from '@/lib/utils';

type GuideRelatedSectionProps = {
  categoryId: string;
  slug: string;
};

export function GuideRelatedSection({ categoryId, slug }: GuideRelatedSectionProps) {
  const [guides, setGuides] = useState<RelatedGuideCard[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    void getRelatedGuides(categoryId, slug).then((rows) => {
      if (cancelled) return;
      setGuides(rows.slice(0, 4));
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [categoryId, slug]);

  if (!loaded || guides.length === 0) return null;

  return (
    <section className="border-t border-gray-100 px-6 py-10 sm:px-10 md:px-12">
      <div className="mb-6 max-w-2xl">
        <h2 className="font-poppins text-xl font-bold text-gray-900 md:text-2xl">
          Continue aprendendo
        </h2>
        <p className="mt-1.5 font-inter text-sm leading-relaxed text-gray-500 md:text-base">
          Guias relacionados que podem ajudar na sua jornada no xat.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {guides.map((guide) => (
          <Link
            key={guide.id}
            href={getGuideHref(guide.categoryId, guide.slug)}
            className={cn(
              'group relative flex h-full flex-col rounded-2xl p-5',
              /* Sem translate no contentor do texto — evita jitter/bounce nas letras. */
              'transition-[border-color,box-shadow,background-color] duration-300',
              'border border-gray-200 bg-gradient-to-b from-white to-gray-50/80 shadow-sm',
              'hover:border-[rgba(0,11,106,0.35)] hover:shadow-md hover:shadow-[0_8px_24px_-12px_rgba(0,11,106,0.25)]'
            )}
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <span
                className={cn(
                  'inline-flex max-w-[80%] truncate rounded-full px-2.5 py-1 font-inter text-[11px] font-semibold',
                  categoryColors[guide.categoryId] ??
                    'bg-[rgba(0,11,106,0.06)] text-[#000b6a] ring-1 ring-[rgba(0,11,106,0.14)]'
                )}
              >
                {guide.categoryName || guide.categoryId}
              </span>
              {guide.featured ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  <Star className="h-3 w-3 fill-amber-600" aria-hidden />
                  Popular
                </span>
              ) : null}
            </div>

            <h3 className="font-poppins text-base font-bold leading-snug text-gray-900 transition-colors duration-200 group-hover:text-[#000b6a]">
              {guide.title}
            </h3>

            {guide.excerpt ? (
              <p className="mt-2 line-clamp-2 font-inter text-sm leading-relaxed text-gray-500">
                {guide.excerpt}
              </p>
            ) : null}

            <div className="mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                <span className="inline-flex items-center gap-1">
                  <BookOpen className="h-3.5 w-3.5" aria-hidden />
                  {formatReadingTimeLabel(guide.readingTime)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" aria-hidden />
                  {formatViewCount(guide.views)}
                </span>
              </div>
              <ArrowRight
                className="h-4 w-4 shrink-0 text-gray-300 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-[#000b6a]"
                aria-hidden
              />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
